import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { X, Sparkles, Copy, Check } from "lucide-react";

const STORAGE_KEY = "luzdeangely_welcome_seen_v1";

export default function WelcomePopup() {
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [coupon, setCoupon] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        const seen = localStorage.getItem(STORAGE_KEY);
        if (seen) return;
        const t = setTimeout(() => setOpen(true), 4000);
        return () => clearTimeout(t);
    }, []);

    function dismiss() {
        localStorage.setItem(STORAGE_KEY, "1");
        setOpen(false);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/subscribers", { email });
            setCoupon(data.coupon_code);
            localStorage.setItem(STORAGE_KEY, "1");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }

    function copyCode() {
        if (!coupon) return;
        navigator.clipboard.writeText(coupon);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[60] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
            onClick={dismiss}
            data-testid="welcome-popup"
        >
            <div
                className="bg-ivory rounded-2xl border border-border-soft/60 w-full max-w-lg overflow-hidden relative animate-[fade-up_0.5s_ease-out]"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={dismiss}
                    className="absolute top-4 right-4 text-taupe hover:text-rose z-10"
                    data-testid="welcome-popup-close"
                    aria-label="Cerrar"
                >
                    <X strokeWidth={1.5} className="w-5 h-5" />
                </button>

                <div className="grid grid-cols-1 md:grid-cols-5">
                    <div className="md:col-span-2 hidden md:block bg-blush">
                        <img
                            src="https://images.unsplash.com/photo-1731586249471-82bb9b2f769a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="md:col-span-3 p-8 md:p-10">
                        {!coupon ? (
                            <>
                                <Sparkles strokeWidth={1.5} className="w-6 h-6 text-rose" />
                                <h2 className="font-serif text-3xl md:text-4xl text-charcoal mt-3 leading-tight">
                                    Tu primer<br />
                                    <span className="italic text-rose">10% de regalo.</span>
                                </h2>
                                <p className="text-sm text-taupe mt-3 leading-relaxed">
                                    Suscríbete y recibe un cupón exclusivo para tu primera compra.
                                </p>
                                <form onSubmit={handleSubmit} className="mt-6 space-y-3" data-testid="welcome-form">
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="tu@email.com"
                                        required
                                        className="w-full bg-white border border-border-soft rounded-full px-5 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose"
                                        data-testid="welcome-email-input"
                                    />
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full bg-rose text-white hover:bg-rose-dark rounded-full px-6 py-3 transition-colors text-xs tracking-[0.2em] uppercase disabled:opacity-50"
                                        data-testid="welcome-submit"
                                    >
                                        {loading ? "Enviando..." : "Quiero mi cupón"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={dismiss}
                                        className="w-full text-xs text-taupe hover:text-rose tracking-wider uppercase"
                                    >
                                        No, gracias
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="text-center py-2">
                                <div className="w-14 h-14 rounded-full bg-blush flex items-center justify-center mx-auto">
                                    <Sparkles strokeWidth={1.5} className="w-7 h-7 text-rose" />
                                </div>
                                <h2 className="font-serif text-3xl text-charcoal mt-4">
                                    ¡Bienvenida!
                                </h2>
                                <p className="text-sm text-taupe mt-2">
                                    Usa este código en tu primera compra:
                                </p>
                                <button
                                    onClick={copyCode}
                                    className="mt-5 inline-flex items-center gap-3 bg-white border-2 border-dashed border-rose rounded-xl px-6 py-3 hover:bg-blush transition"
                                    data-testid="copy-coupon-btn"
                                >
                                    <span className="font-serif text-2xl text-rose tracking-wider">
                                        {coupon}
                                    </span>
                                    {copied ? (
                                        <Check strokeWidth={1.5} className="w-4 h-4 text-rose" />
                                    ) : (
                                        <Copy strokeWidth={1.5} className="w-4 h-4 text-taupe" />
                                    )}
                                </button>
                                <p className="text-xs text-taupe mt-4">
                                    Te lo enviamos también a tu correo.
                                </p>
                                <button
                                    onClick={dismiss}
                                    className="mt-6 text-xs text-taupe hover:text-rose tracking-[0.2em] uppercase"
                                >
                                    Empezar a comprar
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
